import { type InputTypeProps } from "@common/declarations";
import withValueHOC from "@common/hocs/withValue";
import { getFractionalDigits } from "@common/lib/math";
import { isNullOrUndefined } from "@common/lib/misc";
import classnames from "classnames";
import { isObject, isUndefined } from "lodash";
import * as React from "react";
import TextInput, { type TextInputProps } from "../Text/component";
import { formatValue, getFinalValue, isOffBounds } from "./lib";

export type NumberInputConfigProps = {
  addon?: { plural: string; singular: string } | string | React.JSX.Element;
  allowEmpty?: boolean /* in some cases when it's required, we want empty values */;
  fractionalDigits?: number;
  significantDigits?: number;
  emptyValue?: any;
};

export type NumberInputProps = Omit<TextInputProps, "onChange" | "value" | "addon"> &
  InputTypeProps<number | null | undefined> &
  NumberInputConfigProps;
const NumberInputInner = ({ onBlur, onChange, onClick, ...props }: NumberInputProps) => {
  const [val, setVal] = React.useState(formatValue(props.value));
  const [isFocused, setIsFocused] = React.useState(false);

  React.useEffect(() => {
    !isFocused && setVal(formatValue(props.value));
  }, [props.value, isFocused]);

  const _onFocus = React.useCallback(() => {
    setIsFocused(true);
  }, []);

  const _onBlur = React.useCallback(() => {
    setIsFocused(false);

    let finalValue =
      getFinalValue(val, props.min, props.max, props.fractionalDigits, props.allowEmpty) ??
      (props.allowEmpty ? null : props.value);
    finalValue = isNullOrUndefined(finalValue) && "emptyValue" in props ? props.emptyValue : finalValue;
    if (finalValue !== props.value) {
      onChange(finalValue);
    }
    setVal(formatValue(finalValue));

    onBlur && onBlur();
  }, [
    onBlur,
    onChange,
    props.allowEmpty,
    props.emptyValue,
    props.min,
    props.max,
    props.fractionalDigits,
    props.value,
    val
  ]);

  const _onChange = React.useCallback(
    (value: string) => {
      if (!value && props.allowEmpty) {
        onChange("emptyValue" in props ? props.emptyValue : null);
      } else if (value && !isNaN(+value) && !value.endsWith(".")) {
        if (isOffBounds(+value, props.min, props.max)) {
          const fv = getFinalValue(value, props.min, props.max, props.fractionalDigits, true);
          onChange(fv);
        } else {
          onChange(+value);
        }
      }

      setVal(value);
    },
    [
      onChange,
      props.allowEmpty,
      props.emptyValue,
      props.fractionalDigits,
      props.significantDigits,
      props.max,
      props.min
    ]
  );

  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      const step = isUndefined(props.step) ? 1 : +props.step.toString();
      if (e.key === "ArrowDown") {
        e.preventDefault();

        _onChange(((val ? +val : 0) - step).toFixed(getFractionalDigits(step)));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();

        _onChange(((val ? +val : 0) + step).toFixed(getFractionalDigits(step)));
      }
    },
    [_onChange, props.step, val]
  );

  const addon = React.useMemo(() => {
    if (isObject(props.addon) && "plural" in props.addon) {
      return props.value && props.value === 1 ? props.addon.singular : props.addon.plural;
    }

    return props.addon;
  }, [props.value, props.addon]);

  const step = React.useMemo(
    () =>
      props.step ??
      (isUndefined(props.fractionalDigits)
        ? undefined
        : `.${props.fractionalDigits > 1 ? "0".repeat(props.fractionalDigits - 1) : ""}1`),
    [props.step, props.fractionalDigits]
  );

  return (
    <div className={classnames("number_container")} onClick={onClick}>
      <TextInput
        {...props}
        addon={addon}
        className={classnames("number_input", props.className)}
        focusedValue={val.toString()}
        isNoCopy
        onBlur={_onBlur}
        onFocus={_onFocus}
        onChange={_onChange}
        onKeyDown={onKeyDown}
        step={step}
        type="number"
        value={formatValue(val, props.fractionalDigits, props.significantDigits)}
      />
    </div>
  );
};

const NumberInput = withValueHOC<number, NumberInputProps>(NumberInputInner);
export default NumberInput;
